import {BellRing, Loader2, X} from "lucide-react";
import {useState} from "react";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {useStoredMe} from "@/src/modules/core/store/me";
import useNotificationPermission from "@/src/modules/notifications/hooks/use-notification-permissions";
import {
  dismissPushPrompt,
  isPushPromptDismissed,
} from "@/src/modules/notifications/lib/push-prompt-dismiss";

type NotificationEnablePushBannerProps = {
  /** `layout` — full-width app bar; `inline` — inside notification sheet */
  variant?: "layout" | "inline";
  /** When false, hide after user dismisses (7-day TTL). Sheet keeps showing until granted. */
  dismissible?: boolean;
};

export function NotificationEnablePushBanner({
  variant = "inline",
  dismissible = false,
}: NotificationEnablePushBannerProps) {
  const me = useStoredMe();
  const {isSupported, status, isRequesting, requestPermission} = useNotificationPermission();
  const [dismissedLocally, setDismissedLocally] = useState(
    () => dismissible && isPushPromptDismissed(),
  );

  if (
    !isSupported ||
    status === "granted" ||
    isViewingAsUser(me) ||
    (dismissible && dismissedLocally)
  ) {
    return null;
  }

  const isDenied = status === "denied";

  const handleDismiss = () => {
    dismissPushPrompt();
    setDismissedLocally(true);
  };

  return (
    <Alert
      variant="default"
      className={cn(
        "relative",
        variant === "layout"
          ? "rounded-none border-x-0 border-t-0"
          : "mx-4 mb-2",
      )}
    >
      {dismissible ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 size-7"
          aria-label="Dismiss notification prompt"
          onClick={handleDismiss}
        >
          <X className="size-4" />
        </Button>
      ) : null}
      <BellRing className="size-4" />
      <AlertTitle>Enable notifications</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <span className="text-sm text-muted-foreground">
          {isDenied
            ? "Browser notifications are blocked. Allow them in your browser or device settings to get direct messages, briefing updates, and other alerts when the app is in the background."
            : "Get direct messages, briefing board updates, and other alerts when the app is in the background."}
        </span>
        {isDenied ? null : (
          <div className="flex flex-col gap-2">
            {isRequesting ? (
              <p className="text-xs text-muted-foreground" role="status">
                Check your browser for the permission prompt. This may take a moment after you
                allow or block notifications.
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              className="w-fit gap-2"
              disabled={isRequesting}
              aria-busy={isRequesting}
              onClick={() => {
                void requestPermission();
              }}
            >
              {isRequesting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              {isRequesting ? "Enabling…" : "Enable notifications"}
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
